
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { X, UploadCloud, Edit2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

export const KitchenPhotosSection = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [showKitchenPhotos, setShowKitchenPhotos] = useState(false);
  const [photoSelections, setPhotoSelections] = useState<{[key: string]: boolean}>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: verificationData, isLoading } = useQuery({
    queryKey: ['chef-verification'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('cook_verifications')
        .select('*')
        .eq('cook_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    }
  });

  useEffect(() => {
    if (verificationData) {
      // Initialize state from verification data
      setShowKitchenPhotos(verificationData.show_kitchen_photos || false);
      
      // Create a map of photo URLs to their selection state
      const selections: {[key: string]: boolean} = {};
      
      // Process all photo types
      const photoTypes = [
        'kitchen_photos',
        'food_storage_photos',
        'handwashing_photos',
        'food_separation_photos'
      ];
      
      photoTypes.forEach(type => {
        if (verificationData[type] && Array.isArray(verificationData[type])) {
          verificationData[type].forEach((url: string) => {
            // Check if this URL is in the public_kitchen_photos array
            selections[url] = verificationData.public_kitchen_photos?.includes(url) || false;
          });
        }
      });
      
      setPhotoSelections(selections);
    }
  }, [verificationData]);

  const handleToggleVisibility = async (value: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('cook_verifications')
        .update({ show_kitchen_photos: value })
        .eq('cook_id', user.id);

      if (error) throw error;
      
      setShowKitchenPhotos(value);
      await queryClient.invalidateQueries({ queryKey: ['chef-verification'] });
      
      toast({
        title: value ? "Photos Visible" : "Photos Hidden",
        description: value 
          ? "Customers can now see your selected kitchen photos" 
          : "Your kitchen photos are now hidden from your profile",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSelectPhoto = async (url: string, isSelected: boolean) => {
    setPhotoSelections(prev => ({
      ...prev,
      [url]: isSelected
    }));
  };

  const savePhotoSelections = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create an array of selected photo URLs
      const selectedPhotos = Object.entries(photoSelections)
        .filter(([_, isSelected]) => isSelected)
        .map(([url, _]) => url);

      const { error } = await supabase
        .from('cook_verifications')
        .update({ public_kitchen_photos: selectedPhotos })
        .eq('cook_id', user.id);

      if (error) throw error;
      
      await queryClient.invalidateQueries({ queryKey: ['chef-verification'] });
      
      toast({
        title: "Changes Saved",
        description: "Your kitchen photo selections have been updated",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUploadPhoto = async (photoType: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      try {
        setIsUploading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Upload file to storage
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${photoType}/${crypto.randomUUID()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('verification-docs')
          .upload(fileName, file, {
            contentType: file.type,
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('verification-docs')
          .getPublicUrl(fileName);

        // Retrieve existing verification
        const { data: existingVerification } = await supabase
          .from('cook_verifications')
          .select(photoType)
          .eq('cook_id', user.id)
          .single();

        // Update verification record with new photo
        const existingPhotos = existingVerification?.[photoType] || [];
        const updatedPhotos = Array.isArray(existingPhotos) ? [...existingPhotos, publicUrl] : [publicUrl];
        
        const { error } = await supabase
          .from('cook_verifications')
          .update({ [photoType]: updatedPhotos })
          .eq('cook_id', user.id);

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['chef-verification'] });
        
        toast({
          title: "Photo Uploaded",
          description: "Your new kitchen photo has been uploaded",
        });
      } catch (error: any) {
        toast({
          title: "Upload Failed",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    };
    
    input.click();
  };

  const getPhotoTypeLabel = (url: string) => {
    const types = [
      { key: 'kitchen_photos', label: 'Kitchen' },
      { key: 'food_storage_photos', label: 'Food Storage' },
      { key: 'handwashing_photos', label: 'Handwashing Station' },
      { key: 'food_separation_photos', label: 'Food Separation' }
    ];
    
    for (const type of types) {
      if (verificationData && 
          verificationData[type.key] && 
          Array.isArray(verificationData[type.key]) && 
          verificationData[type.key].includes(url)) {
        return type.label;
      }
    }
    
    return 'Other';
  };

  if (isLoading) {
    return <div>Loading kitchen photos...</div>;
  }

  if (!verificationData || verificationData.status !== 'approved') {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-medium">Kitchen Photos</h3>
        <p className="text-muted-foreground mt-2">
          Kitchen photos will be available after your cook verification is approved.
        </p>
      </Card>
    );
  }

  // Get all photos from all categories
  const allPhotos: string[] = [];
  ['kitchen_photos', 'food_storage_photos', 'handwashing_photos', 'food_separation_photos'].forEach(type => {
    if (verificationData[type] && Array.isArray(verificationData[type])) {
      allPhotos.push(...verificationData[type]);
    }
  });

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Kitchen Photos</h3>
        <div className="flex items-center gap-2">
          <Label htmlFor="show-kitchen-photos" className="text-sm">
            Show photos on profile
          </Label>
          <Switch
            id="show-kitchen-photos"
            checked={showKitchenPhotos}
            onCheckedChange={handleToggleVisibility}
          />
        </div>
      </div>
      
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Select which kitchen photos you want to display on your public profile. 
          Customers will only see photos that you've selected and only if you've enabled "Show photos on profile".
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {allPhotos.map((url, index) => (
            <div key={index} className="relative border rounded-lg overflow-hidden group">
              <div className="absolute top-2 left-2 z-10 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {getPhotoTypeLabel(url)}
              </div>
              <div className="absolute top-2 right-2 z-10">
                <Checkbox 
                  checked={photoSelections[url] || false}
                  onCheckedChange={(value) => handleSelectPhoto(url, !!value)}
                  className="bg-white border-gray-300 rounded-sm"
                />
              </div>
              <img 
                src={url} 
                alt={`Kitchen photo ${index + 1}`} 
                className="w-full h-48 object-cover"
              />
            </div>
          ))}
        </div>
        
        <div className="flex justify-between flex-wrap gap-4 mt-4">
          <div className="space-x-2">
            <Button 
              variant="outline" 
              onClick={() => handleUploadPhoto('kitchen_photos')}
              disabled={isUploading}
            >
              <UploadCloud className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading..." : "Add Kitchen Photo"}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => handleUploadPhoto('food_storage_photos')}
              disabled={isUploading}
            >
              <UploadCloud className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading..." : "Add Storage Photo"}
            </Button>
          </div>
          
          <Button onClick={savePhotoSelections}>
            Save Photo Selections
          </Button>
        </div>
      </div>
    </Card>
  );
};
